/**
 * Safe section renderer. Shared by Seiten editor preview AND the public
 * /p/:slug page.
 *
 * Design: section is {type, props}. We render a fixed React component per
 * type and pass props by field — NEVER innerHTML, NEVER eval. React
 * text-nodes already escape, so any user/agent-provided string is inert
 * even if it contains "<script>…</script>".
 */
import React from "react";
import { Sparkles, Package, Check } from "lucide-react";

const FONT_CLASS = {
  jakarta: '"Plus Jakarta Sans", sans-serif',
  inter: '"Inter", sans-serif',
  serif: '"Georgia", serif',
};

const str = (v, fb = "") => (v == null ? fb : String(v));
const absUrl = (u, backendHost) =>
  !u ? "" : (u.startsWith("http") ? u : `${backendHost}${u}`);

export function LandingPageView({ page, products, theme, backendHost, leadForm, onLeadSubmit, isPreview, brand }) {
  const bg = theme?.background === "dark" ? "#0b0f17" : "#ffffff";
  const fg = theme?.background === "dark" ? "#e2e8f0" : "#0f172a";
  const subtle = theme?.background === "dark" ? "#94a3b8" : "#475569";
  const cardBg = theme?.background === "dark" ? "#111827" : "#f8fafc";
  const primary = theme?.primary_color || "#10b981";
  const fontFamily = FONT_CLASS[theme?.font] || FONT_CLASS.jakarta;

  const style = { background: bg, color: fg, fontFamily };
  const brandName = (brand?.name || page?.title || "").toString() || "Marke";
  const brandTagline = brand?.tagline || "";

  return (
    <div style={style} data-testid="landing-page-view" className="min-h-screen">
      {theme?.show_logo && (
        <header className="max-w-5xl mx-auto px-6 py-6 flex items-center gap-3" data-testid="landing-header">
          <div
            className="h-10 w-10 rounded-xl grid place-items-center"
            style={{ background: `${primary}22`, color: primary }}
          >
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="font-bold tracking-tight" style={{ color: fg }}>{brandName}</div>
            {brandTagline && <div className="text-[11px]" style={{ color: subtle }}>{brandTagline}</div>}
          </div>
        </header>
      )}

      <main className="max-w-5xl mx-auto px-6 pb-16 space-y-20">
        {(page.sections || []).map((s) => (
          <SectionRenderer
            key={s.id || `${s.type}-${Math.random()}`}
            section={s}
            products={products}
            theme={{ primary, fg, subtle, cardBg, background: theme?.background }}
            backendHost={backendHost}
            leadForm={leadForm}
            onLeadSubmit={onLeadSubmit}
            isPreview={isPreview}
            brandName={brandName}
          />
        ))}
      </main>
    </div>
  );
}

function SectionRenderer({ section, products, theme, backendHost, leadForm, onLeadSubmit, isPreview, brandName }) {
  const p = section.props || {};
  switch (section.type) {
    case "hero":          return <Hero p={p} theme={theme} backendHost={backendHost} brandName={brandName} />;
    case "product_showcase": return <ProductShowcase p={p} allProducts={products} theme={theme} backendHost={backendHost} />;
    case "features":      return <Features p={p} theme={theme} brandName={brandName} />;
    case "testimonials":  return <Testimonials p={p} theme={theme} />;
    case "lead_form":     return <LeadFormSection p={p} theme={theme} leadForm={leadForm} onLeadSubmit={onLeadSubmit} isPreview={isPreview} />;
    case "cta_banner":    return <CtaBanner p={p} theme={theme} />;
    case "text_block":    return <TextBlock p={p} theme={theme} />;
    case "footer":        return <Footer p={p} theme={theme} brandName={brandName} />;
    default:              return <div style={{ color: theme.subtle }} className="text-sm">Unbekannter Section-Typ: {str(section.type)}</div>;
  }
}

/* ---------- individual sections ---------- */

function Hero({ p, theme, backendHost, brandName }) {
  return (
    <section className="pt-6 lg:pt-12 grid lg:grid-cols-2 gap-10 items-center" data-testid="section-hero">
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: theme.primary }}>{brandName || "Marke"}</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3" style={{ color: theme.fg }}>
          {str(p.headline, "Deine neue Lieblingspflege")}
        </h1>
        <p className="mt-4 text-base" style={{ color: theme.subtle }}>
          {str(p.subline, "Natürliche Inhaltsstoffe. Spürbare Ergebnisse.")}
        </p>
        {p.cta_label && (
          <a
            href="#lead"
            className="inline-block mt-8 px-5 py-2.5 rounded-md font-semibold text-white"
            style={{ background: theme.primary }}
            data-testid="hero-cta"
          >
            {str(p.cta_label)}
          </a>
        )}
      </div>
      {p.image_url && (
        <img
          src={absUrl(p.image_url, backendHost)}
          alt=""
          className="rounded-2xl w-full aspect-[4/5] object-cover"
          data-testid="hero-image"
        />
      )}
    </section>
  );
}

function ProductShowcase({ p, allProducts, theme, backendHost }) {
  const ids = Array.isArray(p.product_ids) && p.product_ids.length ? p.product_ids : (allProducts || []).map((x) => x.id);
  const items = (allProducts || []).filter((x) => ids.includes(x.id));
  if (items.length === 0) return null;
  return (
    <section data-testid="section-product_showcase">
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-6" style={{ color: theme.fg }}>
        {str(p.heading, "Produkte")}
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((prod) => (
          <div key={prod.id} className="rounded-xl overflow-hidden" style={{ background: theme.cardBg }} data-testid={`showcase-product-${prod.id}`}>
            {prod.image_url
              ? <img src={absUrl(prod.image_url, backendHost)} alt="" className="w-full aspect-square object-cover" />
              : <div className="w-full aspect-square grid place-items-center" style={{ background: `${theme.primary}11` }}><Package className="h-8 w-8" style={{ color: theme.primary }} /></div>}
            <div className="p-4">
              <div className="font-semibold" style={{ color: theme.fg }}>{str(prod.name)}</div>
              <div className="text-xs mt-1 line-clamp-2" style={{ color: theme.subtle }}>{str(prod.description)}</div>
              <div className="mt-3 font-bold tabular-nums" style={{ color: theme.primary }}>
                {new Intl.NumberFormat("de-DE", { style: "currency", currency: prod.currency || "EUR" }).format(prod.price || 0)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Features({ p, theme, brandName }) {
  const items = Array.isArray(p.items) ? p.items : [];
  return (
    <section data-testid="section-features">
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-6 text-center" style={{ color: theme.fg }}>
        {str(p.heading, brandName ? `Warum ${brandName}` : "Deine Vorteile")}
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl p-5" style={{ background: theme.cardBg }}>
            <div className="h-10 w-10 rounded-lg grid place-items-center mb-3" style={{ background: `${theme.primary}22`, color: theme.primary }}>
              <Check className="h-5 w-5" />
            </div>
            <div className="font-semibold" style={{ color: theme.fg }}>{str(it.title)}</div>
            <div className="text-sm mt-1" style={{ color: theme.subtle }}>{str(it.text)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Testimonials({ p, theme }) {
  const items = Array.isArray(p.items) ? p.items : [];
  return (
    <section data-testid="section-testimonials">
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-6 text-center" style={{ color: theme.fg }}>
        {str(p.heading, "Was Kundinnen sagen")}
      </h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl p-5" style={{ background: theme.cardBg }}>
            <div className="text-sm italic" style={{ color: theme.fg }}>„{str(it.quote)}"</div>
            <div className="text-xs mt-3 uppercase tracking-wider" style={{ color: theme.primary }}>— {str(it.author)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function LeadFormSection({ p, theme, leadForm, onLeadSubmit, isPreview }) {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", message: "", consent: false, website: "" });
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const fields = (leadForm?.fields || []);
  const consentText = str(leadForm?.consent_text, "Ich willige in die Verarbeitung meiner Daten ein.");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) { setError("Bitte Zustimmung erteilen."); return; }
    if (isPreview) { setSent(true); return; }
    setBusy(true);
    try {
      await onLeadSubmit(form);
      setSent(true);
    } catch (err) {
      setError(err?.message || "Bitte erneut versuchen.");
    } finally { setBusy(false); }
  };

  return (
    <section id="lead" data-testid="section-lead_form">
      <div className="max-w-xl mx-auto rounded-2xl p-6 lg:p-8" style={{ background: theme.cardBg }}>
        <h2 className="text-2xl font-extrabold tracking-tight" style={{ color: theme.fg }}>
          {str(p.heading, "Bleib in Kontakt")}
        </h2>
        {p.intro && <p className="text-sm mt-2" style={{ color: theme.subtle }}>{str(p.intro)}</p>}

        {sent ? (
          <div className="mt-5 text-sm" style={{ color: theme.primary }} data-testid="lead-form-success">
            Danke! Wir melden uns in Kürze.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="lead-form">
            {/* Honeypot — hidden from users */}
            <input
              type="text" tabIndex={-1} autoComplete="off"
              value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
              data-testid="lead-form-honeypot"
              style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
              aria-hidden="true"
            />
            {fields.map((f) => (
              <div key={f.key}>
                <label className="text-xs block mb-1" style={{ color: theme.subtle }}>
                  {str(f.label, f.key)}{f.required ? " *" : ""}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    required={!!f.required}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`lead-form-${f.key}`}
                    className="w-full rounded-md px-3 py-2 text-sm border"
                    style={{ background: theme.background === "dark" ? "rgba(255,255,255,0.05)" : "#fff", borderColor: "rgba(100,116,139,0.3)", color: theme.fg }}
                    rows={3}
                  />
                ) : (
                  <input
                    type={f.type || "text"}
                    required={!!f.required}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`lead-form-${f.key}`}
                    className="w-full rounded-md px-3 py-2 text-sm border"
                    style={{ background: theme.background === "dark" ? "rgba(255,255,255,0.05)" : "#fff", borderColor: "rgba(100,116,139,0.3)", color: theme.fg }}
                  />
                )}
              </div>
            ))}

            <label className="flex gap-2 items-start text-xs mt-1" style={{ color: theme.subtle }}>
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                data-testid="lead-form-consent"
                className="mt-0.5"
              />
              <span>{consentText}</span>
            </label>

            {error && <div className="text-xs" style={{ color: "#ef4444" }} data-testid="lead-form-error">{error}</div>}

            <button
              type="submit" disabled={busy}
              className="w-full px-4 py-2 rounded-md font-semibold text-white"
              style={{ background: theme.primary, opacity: busy ? 0.7 : 1 }}
              data-testid="lead-form-submit"
            >
              {busy ? "Wird gesendet…" : "Absenden"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function CtaBanner({ p, theme }) {
  return (
    <section className="text-center rounded-2xl p-8" style={{ background: `${theme.primary}11` }} data-testid="section-cta_banner">
      <h2 className="text-2xl font-extrabold tracking-tight" style={{ color: theme.fg }}>{str(p.heading, "Jetzt starten")}</h2>
      {p.cta_label && (
        <a href={str(p.cta_url, "#")} className="inline-block mt-5 px-5 py-2.5 rounded-md font-semibold text-white"
          style={{ background: theme.primary }} data-testid="cta-banner-link">
          {str(p.cta_label)}
        </a>
      )}
    </section>
  );
}

function TextBlock({ p, theme }) {
  return (
    <section data-testid="section-text_block" className="max-w-3xl mx-auto">
      {p.heading && <h2 className="text-2xl font-extrabold tracking-tight mb-3" style={{ color: theme.fg }}>{str(p.heading)}</h2>}
      <p className="text-base whitespace-pre-wrap" style={{ color: theme.subtle }}>{str(p.text)}</p>
    </section>
  );
}

function Footer({ p, theme, brandName }) {
  return (
    <footer className="text-center pt-10 border-t" style={{ borderColor: "rgba(100,116,139,0.2)", color: theme.subtle }} data-testid="section-footer">
      <p className="text-xs">{str(p.text, `© ${brandName || "Marke"}`)}</p>
    </footer>
  );
}

export const DEFAULT_SECTIONS = () => ([
  { id: cryptoId(), type: "hero", props: { headline: "Dein Angebot in einem Satz", subline: "Schreib hier, warum deine Besucher JETZT handeln sollten.", cta_label: "Jetzt entdecken" } },
  { id: cryptoId(), type: "product_showcase", props: { heading: "Unsere Highlights", product_ids: [] } },
  { id: cryptoId(), type: "features", props: { heading: "Deine Vorteile", items: [
    { title: "Vorteil 1", text: "Was dein Produkt oder Service besser macht." },
    { title: "Vorteil 2", text: "Worauf du besonders stolz bist." },
    { title: "Vorteil 3", text: "Was deine Kundinnen & Kunden lieben." },
  ] } },
  { id: cryptoId(), type: "lead_form", props: { heading: "Bleib in Kontakt", intro: "Trag dich ein und bleib auf dem Laufenden." } },
  { id: cryptoId(), type: "footer", props: { text: "© Deine Marke · Impressum · Datenschutz" } },
]);

function cryptoId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "s-" + Math.random().toString(36).slice(2);
}
